// components/validationHelpers.js
export const EPS = 0.01; // 1 ban tolerance

export function cents(n) { return Math.round(Number(n) * 100); }
export function fromc(c) { return Math.round(c) / 100; }
export function near(a, b, eps = EPS) { return Math.abs(a - b) <= eps; }
export function isInt(n) { return Number.isInteger(n); }

export function explainBackendError(e) {
  try {
    if (typeof e === "string") {
      try {
        const j = JSON.parse(e);
        return j.detail || j.message || j.error || e;
      } catch {
        return e;
      }
    }
    if (e && typeof e === "object") {
      return e.detail || e.message || e.error || JSON.stringify(e).slice(0, 2000);
    }
  } catch {}
  return "There was an error. Please try again.";
}

/**
 * Each combo line must have selections for every category and priced products where required.
 * @param {Object} line - combo cart line (from state)
 * @param {Object} priceByProductId - pricelist map { [productId]: priceRON }
 * @param {Object} [promoByProductId] - optional promo map { [productId]: promoPriceRON }
 */
export function validateComboSelections(line, priceByProductId, promoByProductId) {
  const missing = [];
  const badPrice = [];
  const badQty = [];

  if (!line.selectionDetails || !Array.isArray(line.selectionDetails)) {
    return { ok: false, reason: "Invalid combo (no selection)." };
  }
  if (!isInt(line.qty) || line.qty <= 0) badQty.push(line.name || line.id);

  for (const s of line.selectionDetails) {
    if (!s || !s.categoryId || !s.productId) {
      missing.push(s?.categoryId || "?");
      continue;
    }
    // Consider item priced if present in pricelist OR in promo map OR priceType is 0/2 (included/fixed)
    const hasListPrice = priceByProductId[s.productId] != null;
    const hasPromo     = promoByProductId && promoByProductId[s.productId] != null;
    const pricedByType = (s.priceType === 0) || (s.priceType === 2);
    const hasPrice = hasListPrice || hasPromo || pricedByType;

    if (!hasPrice) badPrice.push(s.name || s.productId);
  }

  if (missing.length) return { ok: false, reason: `Lipsesc opțiuni pentru categorii: ${missing.join(", ")}` };
  if (badPrice.length) return { ok: false, reason: `Produs fără preț în listă: ${badPrice.join(", ")}` };
  if (badQty.length) return { ok: false, reason: `Cantitate invalidă pentru combo: ${badQty.join(", ")}` };
  return { ok: true };
}

/**
 * Order-level validation just before POST.
 * NOTE: priceByProductId is used only for normal price lines; promo lines don't require pricelist lookup.
 * @param {Object} payload
 * @param {Object} priceByProductId - pricelist map { [productId]: priceRON }
 */
export function validateOrderPayload(payload, priceByProductId) {
  const errs = [];

  if (!payload || typeof payload !== "object") {
    errs.push("Invalid payload.");
    return { ok: false, errors: errs };
  }

  const prods         = Array.isArray(payload.orderProductEntry_order) ? payload.orderProductEntry_order : [];
  const prodsPromo    = Array.isArray(payload.orderProductPromotionEntry_order) ? payload.orderProductPromotionEntry_order : [];
  const combos        = Array.isArray(payload.orderComboEntry_order) ? payload.orderComboEntry_order : [];
  const combosPromo   = Array.isArray(payload.orderComboPromotionEntry_order) ? payload.orderComboPromotionEntry_order : [];

  // Empty cart guard must count ALL four arrays
  if ((prods.length + prodsPromo.length + combos.length + combosPromo.length) === 0) {
    errs.push("Cart is empty.");
  }

  // ---- Normal product lines (ProductPrice) ----
  for (const p of prods) {
    if (!isInt(p.quantity) || p.quantity <= 0) errs.push(`Invalid quantity for the product.`);
    if (!p.ProductPrice?.product) errs.push(`Missing product iref.`);
    // ONLY check pricelist for normal price lines
    if (p.ProductPrice?.product && priceByProductId[p.ProductPrice.product] == null) {
      errs.push(`Product without price in priceList: ${p.ProductPrice.product}`);
    }
    if (typeof p.value !== "number") errs.push(`Invalid value for the product.`);
    if (typeof p.vat !== "number") errs.push(`Invalid VAT for the product`);
  }

  // ---- Promo product lines (ProductPromotion) ----
  for (const p of prodsPromo) {
    if (!isInt(p.quantity) || p.quantity <= 0) errs.push(`Invalid quantity for the product promo.`);
    if (!p.ProductPromotion?.product) errs.push(`Missing product iref.`);
    // Do NOT require pricelist price for promo products
    if (typeof p.value !== "number") errs.push(`Invalid value for the product promo.`);
    if (typeof p.vat !== "number") errs.push(`Invalid VAT for the product promo.`);
  }

  // ---- Normal combo lines (ComboPrice) ----
  for (const c of combos) {
    if (!isInt(c.quantity) || c.quantity <= 0) errs.push(`Invalid quantity for the combo.`);
    if (!c.ComboPrice?.productCombo) errs.push(`Missing combo iref.`);
    if (typeof c.value !== "number") errs.push(`Invalid value for the combo.`);
    if (typeof c.vat !== "number") errs.push(`Invalid VAT for the combo.`);

    const comps = c.OrderComboProductsEntry_OrderComboEntry;
    if (!Array.isArray(comps) || comps.length === 0) {
      errs.push(`Combo without products.`);
    } else {
      const sumAll = comps.reduce((a, b) => a + Number(b.value || 0), 0);
      if (!near(sumAll, c.value, 0.02)) {
        errs.push(`Sum of products is not equal with combo value (${sumAll.toFixed(2)} vs ${Number(c.value).toFixed(2)}).`);
      }
    }
  }

  // ---- Promo combo lines (ComboPromotion) ----
  for (const c of combosPromo) {
    if (!isInt(c.quantity) || c.quantity <= 0) errs.push(`Invalid quantity for the combo promo.`);
    if (!c.ComboPromotion?.productCombo) errs.push(`Missing combo promo iref.`);
    if (typeof c.value !== "number") errs.push(`Invalid value for the combo promo.`);
    if (typeof c.vat !== "number") errs.push(`Invalid VAT for the combo prono.`);

    const comps = c.OrderComboPromotionProductsEntry_OrderComboPromotionEntry;
    if (!Array.isArray(comps) || comps.length === 0) {
      errs.push(`Combo promo without proucts.`);
    } else {
      const sumAll = comps.reduce((a, b) => a + Number(b.value || 0), 0);
      if (!near(sumAll, c.value, 0.02)) {
        errs.push(`Sum of products is not equal with combo promo value (${sumAll.toFixed(2)} vs ${Number(c.value).toFixed(2)}).`);
      }
    }
  }

  const gross = Number(payload.totalAmount || 0);
  const net   = Number(payload.totalAmountWithoutVAT || 0);
  if (!(gross >= 0 && net >= 0)) errs.push("Invalid totals.");
  if (net > gross + EPS) errs.push("Total without VAT is more than total with vAT.");

  return { ok: errs.length === 0, errors: errs };
}